
import React from 'react';
import { CopyrightBundleLayout } from '../../components/CopyrightBundleLayout';
import { Info } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';

export const CopyrightWatermarkScreen: React.FC = () => {
  const { t } = useLanguage();
  return (
    <CopyrightBundleLayout>
        <div className="w-full flex justify-center py-6">
            <h4 className="text-sm font-bold text-slate-900 uppercase tracking-widest mb-8">{t('copyright.watermark.title')}</h4>
        </div>

        <div className="flex-1 w-full max-w-[850px] flex flex-col items-center">
            <div className="relative w-full aspect-[16/10] bg-[#0F172A] rounded-2xl border border-slate-800 shadow-2xl overflow-hidden group">
                {/* Background Image with Grayscale Filter */}
                <div className="absolute inset-0 flex items-center justify-center p-12 opacity-80">
                    <img alt="Luxury Asset" className="w-full h-full object-contain rounded-lg filter grayscale-[20%]" src="https://lh3.googleusercontent.com/aida-public/AB6AXuCOcnCquUa0DF7qPw9iLHk2myTEaO-Jn2TERvEVs5Fe64DBKtdTOmOHY7_gCtIIDiunRW4umAENMelc1TV5eG3qi8IFJ6yKTjVdRQ0l5a60H7Bhxk0KTSYBzuNSRLTFQ0iTx_QbZ_bM4plCAOAmT5uJ_n01npcKzKkufCT7D6-BoBm9ds29yEldEWdYMBi2YVNf2GxEYLEnBQwOT-_BdbEIJ5vK_qdEOpeC0U0xwKsDh26fAMMV5x981qsj4awmysMHkhkaG4_h6EQV"/>
                </div>

                {/* Hidden Watermark Pattern Overlay */}
                <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none">
                    <div className="grid grid-cols-4 gap-20 opacity-40 transform rotate-12">
                        <span className="font-mono text-2xl font-black text-white/5 tracking-[0.5em]">HOMEQUEST</span>
                        <span className="font-mono text-2xl font-black text-white/5 tracking-[0.5em]">HOMEQUEST</span>
                        <span className="font-mono text-2xl font-black text-white/5 tracking-[0.5em]">HOMEQUEST</span>
                        <span className="font-mono text-2xl font-black text-white/5 tracking-[0.5em]">HOMEQUEST</span>
                        <span className="font-mono text-2xl font-black text-white/20 tracking-[0.5em]">HOMEQUEST</span>
                        <span className="font-mono text-2xl font-black text-white/5 tracking-[0.5em]">HOMEQUEST</span>
                        <span className="font-mono text-2xl font-black text-white/5 tracking-[0.5em]">HOMEQUEST</span>
                        <span className="font-mono text-2xl font-black text-white/5 tracking-[0.5em]">HOMEQUEST</span>
                    </div>
                </div>

                {/* Scan Line Animation */}
                <div className="absolute inset-y-0 left-1/3 z-20 w-1 bg-blue-500/50 shadow-[0_0_20px_2px_rgba(37,99,235,0.6)]"></div>

                {/* UI Overlays */}
                <div className="absolute top-6 left-6 z-30">
                    <div className="flex items-center gap-3 bg-black/60 backdrop-blur-md border border-white/10 px-4 py-2 rounded-lg">
                        <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                        <span className="text-[10px] font-mono font-bold text-white tracking-widest uppercase">{t('copyright.watermark.scanning')}</span>
                    </div>
                </div>

                <div className="absolute bottom-6 left-6 z-30 space-y-2">
                    <div className="bg-black/60 backdrop-blur-md border border-white/10 px-4 py-3 rounded-lg">
                        <p className="text-[9px] font-bold text-blue-400 uppercase tracking-widest mb-1">{t('copyright.watermark.status')}</p>
                        <p className="text-xs font-bold text-white">{t('copyright.watermark.detected')}</p>
                    </div>
                    <div className="bg-black/60 backdrop-blur-md border border-white/10 px-4 py-3 rounded-lg">
                        <p className="text-[9px] font-bold text-blue-400 uppercase tracking-widest mb-1">{t('copyright.watermark.score')}</p>
                        <div className="flex items-end gap-2">
                            <span className="text-xl font-mono font-bold text-white">99.8%</span>
                            <span className="text-[9px] text-slate-400 mb-1">{t('copyright.watermark.stable')}</span>
                        </div>
                    </div>
                </div>

                <div className="absolute top-6 right-6 w-56 z-30">
                    <div className="bg-black/60 backdrop-blur-md border border-white/10 p-5 rounded-xl">
                        <h4 className="text-[10px] font-bold text-white uppercase tracking-widest mb-3 flex items-center gap-2">
                            <Info style={{ fontSize: 14 }} className="text-blue-400" />
                            {t('copyright.watermark.encodingTitle')}
                        </h4>
                        <p className="text-[11px] leading-relaxed text-slate-300 font-medium">
                            {t('copyright.watermark.encodingDesc')}
                        </p>
                        <div className="mt-4 pt-4 border-t border-white/10">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-[9px] text-slate-500 font-bold uppercase">{t('copyright.watermark.latentSeed')}</span>
                                <span className="text-[9px] font-mono text-slate-300">#0x8832LW</span>
                            </div>
                            <div className="w-full bg-slate-800 h-1 rounded-full overflow-hidden">
                                <div className="bg-blue-500 h-full w-[98%]"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="w-full mt-8 flex justify-between items-end">
                <div className="space-y-1">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{t('copyright.watermark.engineSig')}</span>
                    <div className="flex items-center gap-3">
                        <span className="text-xs font-mono font-bold text-slate-600">DeepScan Neural Engine v6.1</span>
                        <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-100">STABLE</span>
                    </div>
                </div>
                <div className="flex gap-4">
                    <div className="text-right">
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{t('copyright.watermark.inference')}</span>
                        <p className="text-xs font-mono font-bold text-slate-800">42ms</p>
                    </div>
                    <div className="text-right">
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{t('copyright.watermark.confidence')}</span>
                        <p className="text-xs font-mono font-bold text-slate-800">99.999%</p>
                    </div>
                </div>
            </div>
        </div>
    </CopyrightBundleLayout>
  );
};
